import type { VideoReference } from '@/api/types';

/** Площадки embed (PLAY-5); неизвестная — заглушка. */
export const EMBED_PLATFORMS = ['youtube', 'instagram', 'tiktok', 'facebook'] as const;
export type EmbedPlatform = (typeof EMBED_PLATFORMS)[number];

export function isEmbedPlatform(value: string): value is EmbedPlatform {
  return (EMBED_PLATFORMS as readonly string[]).includes(value);
}

/** id ролика YouTube из ссылки: watch?v=, youtu.be/, /shorts/, /embed/. */
export function youtubeIdFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('/')[0] || null;
    if (/(^|\.)youtube\.com$/.test(u.hostname)) {
      const v = u.searchParams.get('v');
      if (v) return v;
      const m = u.pathname.match(/^\/(shorts|embed|live)\/([^/?]+)/);
      if (m?.[2]) return m[2];
    }
  } catch {
    // не URL
  }
  return null;
}

/** Адрес iframe — тот же формат, что в текущем SPA (embedUtils.getEmbedPreviewUrl). */
export function buildEmbedUrl(
  video: Pick<VideoReference, 'platform' | 'source_url' | 'platform_video_id'>,
  autoplay = false,
): string | null {
  switch (video.platform) {
    case 'instagram': {
      const clean = (video.source_url.split('?')[0] ?? '').replace(/\/$/, '');
      return clean ? `${clean}/embed${autoplay ? '/?autoplay=1' : ''}` : null;
    }
    case 'youtube': {
      const id = video.platform_video_id ?? youtubeIdFromUrl(video.source_url);
      if (!id) return null;
      const base = `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;
      return autoplay ? `${base}&autoplay=1` : base;
    }
    case 'tiktok': {
      if (!video.platform_video_id) return null;
      const base = `https://www.tiktok.com/player/v1/${video.platform_video_id}?music_info=0&description=0`;
      return autoplay ? `${base}&autoplay=1` : base;
    }
    case 'facebook': {
      const base = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(video.source_url)}&show_text=false`;
      return autoplay ? `${base}&autoplay=true` : base;
    }
    default:
      return null;
  }
}

/**
 * Площадки отдают в iframe целую страницу поста: шапка с автором сверху,
 * реакции и подпись снизу. Кадр остаётся в своём размере — iframe делается выше
 * на эти поля и сдвигается вверх, лишнее срезает `overflow` сцены.
 * Значения — как в текущем SPA (`embedUtils.getEmbedIframeStyle`).
 */
const EMBED_CHROME: Partial<Record<EmbedPlatform, { top: number; bottom: number }>> = {
  instagram: { top: 58, bottom: 90 },
  facebook: { top: 44, bottom: 50 },
  tiktok: { top: 10, bottom: 10 },
};

export function embedChrome(platform: string): { top: number; bottom: number } {
  return (isEmbedPlatform(platform) ? EMBED_CHROME[platform] : null) ?? { top: 0, bottom: 0 };
}

/**
 * Пропорции сцены плеера: реальные размеры файла, иначе ориентация из разметки
 * (вертикаль 9:16, горизонталь 16:9). Кадр занимает сцену целиком, а не висит
 * маркой посреди 16:9.
 */
export function stageAspect(video: Pick<VideoReference, 'media' | 'orientation'>): string {
  const width = video.media?.width ?? null;
  const height = video.media?.height ?? null;
  if (width && height) return `${width} / ${height}`;
  return video.orientation === 'vertical' ? '9 / 16' : '16 / 9';
}

/**
 * Пропорции того, что площадка реально рисует в iframe. Instagram кадрирует
 * вертикаль до 4:5 — если строить сцену по 9:16, снизу остаётся полоса с
 * подвалом поста (лайки, «Добавьте комментарий»). Берём на полтора процента
 * шире 4:5: кадр чуть выше окна, и верхняя кромка подвала уходит за обрез —
 * ровно 4:5 оставляет её видимой на пару пикселей. Остальные площадки отдают
 * кадр как есть.
 */
const INSTAGRAM_VERTICAL_AR = '812 / 1000';

export function embedAspect(
  video: Pick<VideoReference, 'media' | 'orientation' | 'platform'>,
): string {
  if (video.platform === 'instagram' && video.orientation === 'vertical')
    return INSTAGRAM_VERTICAL_AR;
  return stageAspect(video);
}
